import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BiChevronDown, BiChevronUp, BiSolidInbox } from 'react-icons/bi';
import { ApiError, type ApiErrorDetail } from '@/api/errors';
import { Banner, type BannerTone } from '@/components/common/Banner';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { StatusChip } from '@/components/common/StatusChip';
import { FormField } from '@/components/form/FormField';
import { DataTable, type DataTableColumn } from '@/components/table/DataTable';
import { EmptyState } from '@/components/table/EmptyState';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import './ContentEntityManager.css';

/**
 * Base shape every website-content row satisfies (`ParentOpinion` / `FaqEntry` —
 * `packages/shared/src/contracts/website-content.ts`). Translatable fields are read via a
 * `Record<string, unknown>` cast (see `fieldValue` below) so this ONE manager can drive both
 * entities from a declarative field list, instead of duplicating the CRUD/reorder/activate/
 * delete/table/form wiring per entity (T140 — "reusable manager per entity", mirrors
 * `LookupManager` being reused across three lookup resources).
 */
export interface ContentEntityRow {
  id: number;
  isActive: boolean;
  sortOrder: number;
}

/** One AR/EN translatable field pair, e.g. `{ key: 'name' }` -> reads/writes `nameAr`/`nameEn`. */
export interface ContentFieldConfig {
  key: string;
  /** Server-enforced length cap (T136/DTO `@MaxLength`), mirrored client-side (AC-2). */
  maxLength: number;
  /** `true` renders a `<textarea>` (quote/answer); `false`/omitted renders a single-line input. */
  multiline?: boolean;
}

export interface ContentEntityConfig<T extends ContentEntityRow> {
  /** i18n namespace for this entity's own copy — `contentSettings.<i18nKey>.*` (fields/columns/
   * tableCaption). Everything else (buttons, confirms, empty states, generic errors) is shared
   * under `contentSettings.common.*`. */
  i18nKey: string;
  fields: ContentFieldConfig[];
  api: {
    list: () => Promise<T[]>;
    /** Payload keys are exactly `${field.key}Ar`/`${field.key}En` for every configured field,
     * built by this manager — safe to forward as the entity's concrete Upsert request type. */
    create: (payload: Record<string, unknown>) => Promise<T>;
    update: (id: number, payload: Record<string, unknown>) => Promise<T>;
    remove: (id: number) => Promise<void>;
    reorder: (ids: number[]) => Promise<T[]>;
  };
  /** Short human-readable identity for a row (toasts/confirm dialogs/aria-labels), in the given
   * UI language — e.g. the parent's name, or a truncated FAQ question. */
  summary: (row: T, language: string) => string;
}

type FormValues = Record<string, string>;
type FormErrors = Record<string, string | undefined>;

function fieldValue(row: ContentEntityRow, field: string): string {
  return String((row as unknown as Record<string, unknown>)[field] ?? '');
}

function buildFormValues<T extends ContentEntityRow>(
  fields: ContentFieldConfig[],
  row?: T | null,
): FormValues {
  const values: FormValues = {};
  for (const field of fields) {
    values[`${field.key}Ar`] = row ? fieldValue(row, `${field.key}Ar`) : '';
    values[`${field.key}En`] = row ? fieldValue(row, `${field.key}En`) : '';
  }
  return values;
}

interface ContentEntityManagerProps<T extends ContentEntityRow> {
  config: ContentEntityConfig<T>;
}

/**
 * Reusable Settings → Content manager (T140) — one instance per entity tab
 * (`ContentSettingsPage`), config-driven so Parents' Opinions (name/role/quote) and FAQ
 * (question/answer) share every behavior: list (active+inactive, `sortOrder` order), create/edit
 * via a bilingual AR+EN form, activate/deactivate, hard delete behind a `ConfirmDialog`, and
 * reorder via up/down controls that PATCH the entity's FULL ordered id list (Assumption C/FR-8).
 * Structure mirrors `LookupManager.tsx` (toolbar → optional form panel → `DataTable` → confirm
 * dialogs); the multi-field bilingual panel borrows `SchoolCostManager`/`ExamPricingManager`'s
 * inline-`fa-form-section`-above-the-table convention rather than `LookupManager`'s in-row
 * editing, since a Parent Opinion has 6 text fields (too cramped for an inline table cell).
 */
export function ContentEntityManager<T extends ContentEntityRow>({
  config,
}: ContentEntityManagerProps<T>) {
  const { t, i18n } = useTranslation();
  const prefix = `contentSettings.${config.i18nKey}`;

  const [items, setItems] = useState<T[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [banner, setBanner] = useState<{ tone: BannerTone; text: string } | null>(null);
  const [searchInput, setSearchInput] = useState('');

  const [panelMode, setPanelMode] = useState<'create' | 'edit' | null>(null);
  const [panelRow, setPanelRow] = useState<T | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({});
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const [activatingId, setActivatingId] = useState<number | null>(null);
  const [reorderingId, setReorderingId] = useState<number | null>(null);
  const [confirmDeactivateRow, setConfirmDeactivateRow] = useState<T | null>(null);
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);
  const [confirmDeleteRow, setConfirmDeleteRow] = useState<T | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    config.api
      .list()
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => {
        if (!cancelled) setLoadError(t('contentSettings.common.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `config` is a stable module-level constant per tab (see `ContentSettingsPage`) — only
    // `reloadToken`/`t` should re-trigger the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadToken, t]);

  function refetch() {
    setReloadToken((v) => v + 1);
  }

  function genericErrorMessage(apiError: ApiError): string {
    return apiError.code === 'NETWORK_ERROR'
      ? t('contentSettings.common.networkError')
      : apiError.message || t('contentSettings.common.genericError');
  }

  const filtered = (items ?? []).filter((row) => {
    const needle = searchInput.trim().toLocaleLowerCase();
    if (!needle) return true;
    return config.fields.some(
      (field) =>
        fieldValue(row, `${field.key}Ar`).toLocaleLowerCase().includes(needle) ||
        fieldValue(row, `${field.key}En`).toLocaleLowerCase().includes(needle),
    );
  });

  const rankById = new Map<number, number>();
  (items ?? []).forEach((row, index) => rankById.set(row.id, index + 1));

  function validate(values: FormValues): FormErrors {
    const errors: FormErrors = {};
    for (const field of config.fields) {
      for (const lang of ['Ar', 'En'] as const) {
        const key = `${field.key}${lang}`;
        const value = (values[key] ?? '').trim();
        const label = t(`${prefix}.fields.${key}`);
        if (!value) {
          errors[key] = t('contentSettings.common.requiredError', { field: label });
        } else if (value.length > field.maxLength) {
          errors[key] = t('contentSettings.common.tooLongError', {
            field: label,
            max: field.maxLength,
          });
        }
      }
    }
    return errors;
  }

  function fieldErrorsFromApi(details: ApiErrorDetail[]): FormErrors {
    const errors: FormErrors = {};
    for (const detail of details) {
      if (detail.field && detail.field in formValues) errors[detail.field] = detail.message;
    }
    return errors;
  }

  function openCreatePanel() {
    setBanner(null);
    setPanelMode('create');
    setPanelRow(null);
    setFormValues(buildFormValues(config.fields));
    setFormErrors({});
  }

  function openEditPanel(row: T) {
    setBanner(null);
    setPanelMode('edit');
    setPanelRow(row);
    setFormValues(buildFormValues(config.fields, row));
    setFormErrors({});
  }

  function closePanel() {
    setPanelMode(null);
    setPanelRow(null);
    setFormErrors({});
  }

  function updateFieldValue(key: string, value: string) {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBanner(null);

    const errors = validate(formValues);
    setFormErrors(errors);
    const firstErrorKey = Object.keys(errors).find((key) => errors[key]);
    if (firstErrorKey) {
      document.getElementById(`content-field-${firstErrorKey}`)?.focus();
      return;
    }

    const payload: Record<string, unknown> = {};
    for (const field of config.fields) {
      payload[`${field.key}Ar`] = formValues[`${field.key}Ar`].trim();
      payload[`${field.key}En`] = formValues[`${field.key}En`].trim();
    }

    setSubmitting(true);
    try {
      if (panelMode === 'create') {
        const created = await config.api.create(payload);
        const message = t('contentSettings.common.createSuccess', {
          label: config.summary(created, i18n.language),
        });
        setBanner({ tone: 'success', text: message });
        showSuccessToast(message);
      } else if (panelMode === 'edit' && panelRow) {
        const updated = await config.api.update(panelRow.id, payload);
        const message = t('contentSettings.common.updateSuccess', {
          label: config.summary(updated, i18n.language),
        });
        setBanner({ tone: 'success', text: message });
        showSuccessToast(message);
      }
      closePanel();
      refetch();
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      const apiFieldErrors = fieldErrorsFromApi(apiError.details);
      if (Object.keys(apiFieldErrors).length > 0) {
        setFormErrors((prev) => ({ ...prev, ...apiFieldErrors }));
      } else {
        const message = genericErrorMessage(apiError);
        setBanner({ tone: 'danger', text: message });
        if (apiError.code === 'NETWORK_ERROR') showErrorToast(message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  function fieldPayloadFrom(row: T): Record<string, unknown> {
    const payload: Record<string, unknown> = {};
    for (const field of config.fields) {
      payload[`${field.key}Ar`] = fieldValue(row, `${field.key}Ar`);
      payload[`${field.key}En`] = fieldValue(row, `${field.key}En`);
    }
    return payload;
  }

  async function handleActivate(row: T) {
    setBanner(null);
    setActivatingId(row.id);
    try {
      const updated = await config.api.update(row.id, { ...fieldPayloadFrom(row), isActive: true });
      const message = t('contentSettings.common.activateSuccess', {
        label: config.summary(updated, i18n.language),
      });
      setBanner({ tone: 'success', text: message });
      showSuccessToast(message);
      refetch();
    } catch (err) {
      setBanner({ tone: 'danger', text: genericErrorMessage(ApiError.fromUnknown(err)) });
    } finally {
      setActivatingId(null);
    }
  }

  async function handleConfirmDeactivate() {
    if (!confirmDeactivateRow) return;
    setBanner(null);
    setDeactivateSubmitting(true);
    try {
      const updated = await config.api.update(confirmDeactivateRow.id, {
        ...fieldPayloadFrom(confirmDeactivateRow),
        isActive: false,
      });
      const message = t('contentSettings.common.deactivateSuccess', {
        label: config.summary(updated, i18n.language),
      });
      setBanner({ tone: 'success', text: message });
      showSuccessToast(message);
      setConfirmDeactivateRow(null);
      refetch();
    } catch (err) {
      setBanner({ tone: 'danger', text: genericErrorMessage(ApiError.fromUnknown(err)) });
      setConfirmDeactivateRow(null);
    } finally {
      setDeactivateSubmitting(false);
    }
  }

  async function handleConfirmDelete() {
    if (!confirmDeleteRow) return;
    setBanner(null);
    setDeleteSubmitting(true);
    const label = config.summary(confirmDeleteRow, i18n.language);
    try {
      await config.api.remove(confirmDeleteRow.id);
      const message = t('contentSettings.common.deleteSuccess', { label });
      setBanner({ tone: 'success', text: message });
      showSuccessToast(message);
      setConfirmDeleteRow(null);
      refetch();
    } catch (err) {
      setBanner({ tone: 'danger', text: genericErrorMessage(ApiError.fromUnknown(err)) });
      setConfirmDeleteRow(null);
    } finally {
      setDeleteSubmitting(false);
    }
  }

  async function handleMove(row: T, direction: 'up' | 'down') {
    if (!items) return;
    const currentIndex = items.findIndex((r) => r.id === row.id);
    if (currentIndex === -1) return;
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return;

    // Full ordered id list (active + inactive) with the two positions swapped — the reorder
    // endpoint requires the entity's ENTIRE id set, never a partial/visible-only subset
    // (Assumption C/FR-8; a mismatched set 400s `REORDER_IDS_MISMATCH`).
    const reorderedIds = items.map((r) => r.id);
    const tmp = reorderedIds[currentIndex];
    reorderedIds[currentIndex] = reorderedIds[targetIndex];
    reorderedIds[targetIndex] = tmp;

    setBanner(null);
    setReorderingId(row.id);
    try {
      const result = await config.api.reorder(reorderedIds);
      setItems(result);
      showSuccessToast(t('contentSettings.common.reorderSuccess'));
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      if (apiError.code === 'REORDER_IDS_MISMATCH') {
        // The row set changed under us (another admin/tab added or deleted a row), so the id list
        // we just sent is stale. Without a refetch every subsequent up/down would 400 identically
        // until a manual page reload — reload the list so the next reorder sends the current set.
        setBanner({ tone: 'warning', text: t('contentSettings.common.reorderStaleList') });
        refetch();
      } else {
        setBanner({ tone: 'danger', text: genericErrorMessage(apiError) });
      }
    } finally {
      setReorderingId(null);
    }
  }

  const fieldColumns: DataTableColumn<T>[] = config.fields.map((field) => ({
    key: field.key,
    header: t(`${prefix}.columns.${field.key}`),
    render: (row) => (
      <div className="fa-content-manager__cell">
        <bdi className="fa-content-manager__cell-ar">{fieldValue(row, `${field.key}Ar`)}</bdi>
        <bdi className="fa-content-manager__cell-en fa-ltr-isolate">
          {fieldValue(row, `${field.key}En`)}
        </bdi>
      </div>
    ),
  }));

  const columns: DataTableColumn<T>[] = [
    {
      key: 'position',
      header: t('contentSettings.common.columns.position'),
      render: (row) => rankById.get(row.id) ?? '—',
    },
    ...fieldColumns,
    {
      key: 'status',
      header: t('contentSettings.common.columns.status'),
      render: (row) => (
        <StatusChip
          label={
            row.isActive
              ? t('contentSettings.common.status.active')
              : t('contentSettings.common.status.inactive')
          }
          tone={row.isActive ? 'success' : 'neutral'}
        />
      ),
    },
    {
      key: 'actions',
      header: t('contentSettings.common.columns.actions'),
      cellClassName: 'fa-content-manager__actions-cell',
      render: (row) => {
        const label = config.summary(row, i18n.language);
        const rank = rankById.get(row.id) ?? 1;
        const isFirst = rank <= 1;
        const isLast = rank >= (items?.length ?? 1);
        const reorderBusy = reorderingId !== null;

        return (
          <div className="fa-content-manager__row-actions">
            <div className="fa-content-manager__reorder-buttons">
              <button
                type="button"
                className="fa-content-manager__icon-btn"
                aria-label={t('contentSettings.common.moveUpAriaLabel', { label })}
                disabled={isFirst || reorderBusy}
                onClick={() => handleMove(row, 'up')}
              >
                <BiChevronUp aria-hidden="true" />
              </button>
              <button
                type="button"
                className="fa-content-manager__icon-btn"
                aria-label={t('contentSettings.common.moveDownAriaLabel', { label })}
                disabled={isLast || reorderBusy}
                onClick={() => handleMove(row, 'down')}
              >
                <BiChevronDown aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className="fa-btn fa-btn--ghost"
              aria-label={t('contentSettings.common.editAriaLabel', { label })}
              onClick={() => openEditPanel(row)}
            >
              {t('contentSettings.common.editButton')}
            </button>
            {row.isActive ? (
              <button
                type="button"
                className="fa-btn fa-btn--secondary"
                aria-label={t('contentSettings.common.deactivateAriaLabel', { label })}
                onClick={() => setConfirmDeactivateRow(row)}
              >
                {t('contentSettings.common.deactivateButton')}
              </button>
            ) : (
              <button
                type="button"
                className="fa-btn fa-btn--secondary"
                aria-label={t('contentSettings.common.activateAriaLabel', { label })}
                disabled={activatingId === row.id}
                onClick={() => handleActivate(row)}
              >
                {activatingId === row.id
                  ? t('contentSettings.common.savingButton')
                  : t('contentSettings.common.activateButton')}
              </button>
            )}
            <button
              type="button"
              className="fa-btn fa-btn--destructive"
              aria-label={t('contentSettings.common.deleteAriaLabel', { label })}
              onClick={() => setConfirmDeleteRow(row)}
            >
              {t('contentSettings.common.deleteButton')}
            </button>
          </div>
        );
      },
    },
  ];

  const hasSearch = Boolean(searchInput.trim());
  const emptyState = hasSearch ? (
    <EmptyState
      icon={BiSolidInbox}
      title={t('contentSettings.common.empty.noMatchesTitle')}
      hint={t('contentSettings.common.empty.noMatchesHint')}
      action={
        <button
          type="button"
          className="fa-btn fa-btn--secondary"
          onClick={() => setSearchInput('')}
        >
          {t('contentSettings.common.empty.clearSearch')}
        </button>
      }
    />
  ) : (
    <EmptyState
      icon={BiSolidInbox}
      title={t('contentSettings.common.empty.noRecordsTitle')}
      hint={t('contentSettings.common.empty.noRecordsHint')}
    />
  );

  const panelTitle =
    panelMode === 'create'
      ? t('contentSettings.common.addPanelTitle')
      : t('contentSettings.common.editPanelTitle');

  return (
    <div className="fa-content-manager">
      {banner && (
        <Banner tone={banner.tone} assertive={banner.tone === 'danger'}>
          {banner.text}
        </Banner>
      )}

      {panelMode === null && (
        <div className="fa-content-manager__toolbar-top">
          <button type="button" className="fa-btn fa-btn--primary" onClick={openCreatePanel}>
            {t('contentSettings.common.addButton')}
          </button>
        </div>
      )}

      {panelMode !== null && (
        <form
          className="fa-content-manager__panel fa-form-section"
          onSubmit={handleSubmit}
          noValidate
        >
          <h2 className="fa-form-section__title">{panelTitle}</h2>

          {config.fields.map((field) => {
            const arKey = `${field.key}Ar`;
            const enKey = `${field.key}En`;
            const arValue = formValues[arKey] ?? '';
            const enValue = formValues[enKey] ?? '';
            return (
              <div className="fa-field-row" key={field.key}>
                <FormField
                  id={`content-field-${arKey}`}
                  label={t(`${prefix}.fields.${arKey}`)}
                  required
                  error={formErrors[arKey]}
                  hint={`${arValue.length}/${field.maxLength}`}
                >
                  {field.multiline ? (
                    <textarea
                      dir="rtl"
                      maxLength={field.maxLength}
                      value={arValue}
                      disabled={submitting}
                      onChange={(e) => updateFieldValue(arKey, e.target.value)}
                    />
                  ) : (
                    <input
                      type="text"
                      dir="rtl"
                      maxLength={field.maxLength}
                      value={arValue}
                      disabled={submitting}
                      onChange={(e) => updateFieldValue(arKey, e.target.value)}
                    />
                  )}
                </FormField>
                <FormField
                  id={`content-field-${enKey}`}
                  label={t(`${prefix}.fields.${enKey}`)}
                  required
                  error={formErrors[enKey]}
                  hint={`${enValue.length}/${field.maxLength}`}
                >
                  {field.multiline ? (
                    <textarea
                      dir="ltr"
                      className="fa-ltr-isolate"
                      maxLength={field.maxLength}
                      value={enValue}
                      disabled={submitting}
                      onChange={(e) => updateFieldValue(enKey, e.target.value)}
                    />
                  ) : (
                    <input
                      type="text"
                      dir="ltr"
                      className="fa-ltr-isolate"
                      maxLength={field.maxLength}
                      value={enValue}
                      disabled={submitting}
                      onChange={(e) => updateFieldValue(enKey, e.target.value)}
                    />
                  )}
                </FormField>
              </div>
            );
          })}

          <div className="fa-form-actions">
            <button
              type="button"
              className="fa-btn fa-btn--secondary"
              disabled={submitting}
              onClick={closePanel}
            >
              {t('contentSettings.common.cancelButton')}
            </button>
            <button type="submit" className="fa-btn fa-btn--primary" disabled={submitting}>
              {submitting
                ? t('contentSettings.common.savingButton')
                : t('contentSettings.common.saveButton')}
            </button>
          </div>
        </form>
      )}

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(row) => String(row.id)}
        caption={t(`${prefix}.tableCaption`)}
        meta={null}
        loading={loading}
        error={loadError}
        onRetry={refetch}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: t('contentSettings.common.searchPlaceholder'),
          ariaLabel: t('contentSettings.common.searchAriaLabel'),
        }}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        showExportButton={false}
        emptyState={emptyState}
      />

      <ConfirmDialog
        open={Boolean(confirmDeactivateRow)}
        title={t('contentSettings.common.deactivateConfirm.title')}
        description={
          confirmDeactivateRow
            ? t('contentSettings.common.deactivateConfirm.body', {
                label: config.summary(confirmDeactivateRow, i18n.language),
              })
            : undefined
        }
        confirmLabel={t('contentSettings.common.deactivateConfirm.confirm')}
        cancelLabel={t('contentSettings.common.deactivateConfirm.cancel')}
        confirmDisabled={deactivateSubmitting}
        onConfirm={handleConfirmDeactivate}
        onCancel={() => setConfirmDeactivateRow(null)}
      />

      <ConfirmDialog
        open={Boolean(confirmDeleteRow)}
        title={t('contentSettings.common.deleteConfirm.title')}
        description={
          confirmDeleteRow
            ? t('contentSettings.common.deleteConfirm.body', {
                label: config.summary(confirmDeleteRow, i18n.language),
              })
            : undefined
        }
        confirmLabel={t('contentSettings.common.deleteConfirm.confirm')}
        cancelLabel={t('contentSettings.common.deleteConfirm.cancel')}
        destructive
        confirmDisabled={deleteSubmitting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteRow(null)}
      />
    </div>
  );
}
